/**
 * ItemGranolaSyncPanel
 *
 * Allows a user to attach a priorities.ai item to a Granola meeting note,
 * documenting where the item was discussed or decided.
 * Writes the mapping to entity_external_ids so the sync pipeline can track it.
 *
 * Displayed in the Classification tab of ItemDetailPage when Granola is connected.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  Search,
  X,
  AlertCircle,
  Check,
  FileText,
  Calendar,
} from 'lucide-react';
import { getIntegrationsApi, type GranolaNote } from '../../../lib/integrationsApi';
import { registerExternalId, getExternalIds } from '../../../lib/connectors';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';

// ── Granola icon ──────────────────────────────────────────────────────────────

const GranolaIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="4" fill="#16A34A" />
    <rect x="6" y="7" width="12" height="2" rx="1" fill="white" />
    <rect x="6" y="11" width="9" height="2" rx="1" fill="white" />
    <rect x="6" y="15" width="10" height="2" rx="1" fill="white" />
    <circle cx="18" cy="16" r="2.5" fill="#BBF7D0" />
    <path d="M17 16L17.8 16.8L19.2 15.4" stroke="#16A34A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExistingLink {
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  mappingId?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

interface ItemGranolaSyncPanelProps {
  itemId: string;
  itemTitle?: string;
}

export function ItemGranolaSyncPanel({ itemId, itemTitle }: ItemGranolaSyncPanelProps) {
  const workspaceId = getCurrentWorkspaceId();

  // ── Existing links ───────────────────────────────────────────────────────
  const [links, setLinks] = useState<ExistingLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);

  // ── Panel state ──────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [granolaConnected, setGranolaConnected] = useState<boolean | null>(null);

  // ── Search ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [notes, setNotes] = useState<GranolaNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState<GranolaNote | null>(null);

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing Granola links for this item
  const loadLinks = useCallback(async () => {
    if (!workspaceId) return;
    setLinksLoading(true);
    try {
      const allLinks = await getExternalIds(workspaceId, 'item', itemId);
      const gLinks = allLinks.filter((l) => l.connectorType === 'granola');

      const supabase = getSafeSupabaseClient();
      if (supabase && gLinks.length > 0) {
        const { data } = await supabase
          .from('entity_external_ids')
          .select('id, external_id')
          .eq('workspace_id', workspaceId)
          .eq('entity_type', 'item')
          .eq('internal_id', itemId)
          .eq('connector_type', 'granola')
          .eq('is_active', true);

        const idMap: Record<string, string> = {};
        for (const row of data ?? []) {
          idMap[row.external_id as string] = row.id as string;
        }
        setLinks(gLinks.map((l) => ({ ...l, mappingId: idMap[l.externalId] })));
      } else {
        setLinks(gLinks);
      }
    } finally {
      setLinksLoading(false);
    }
  }, [workspaceId, itemId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // When panel opens, check connection and load recent notes
  useEffect(() => {
    if (!isOpen || !workspaceId) return;

    getIntegrationsApi(workspaceId)
      .getGranolaStatus()
      .then((status) => {
        setGranolaConnected(status.connected);
        if (!status.connected) return;

        // Load initial set of recent notes
        setNotesLoading(true);
        getIntegrationsApi(workspaceId)
          .searchGranolaNotes({ limit: 30 })
          .then(({ notes: n }) => setNotes(n))
          .catch(() => {/* silent — search will show error */})
          .finally(() => setNotesLoading(false));
      })
      .catch(() => setGranolaConnected(false));
  }, [isOpen, workspaceId]);

  // Search notes with debounce
  useEffect(() => {
    if (!isOpen || !granolaConnected) return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setNotesLoading(true);
      setError(null);
      getIntegrationsApi(workspaceId ?? 'default')
        .searchGranolaNotes({ search: searchQuery || undefined, limit: 30 })
        .then(({ notes: n }) => setNotes(n))
        .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load notes'))
        .finally(() => setNotesLoading(false));
    }, 350);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, isOpen, granolaConnected, workspaceId]);

  const handleOpen = () => {
    setIsOpen(true);
    setSelectedNote(null);
    setSearchQuery('');
    setError(null);
    setSaveSuccess(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedNote(null);
    setSearchQuery('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedNote || !workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: itemId,
        connectorType: 'granola',
        externalId: selectedNote.id,
        externalUrl: selectedNote.url ?? `https://app.granola.ai/notes/${selectedNote.id}`,
      });
      setSaveSuccess(true);
      await loadLinks();
      setTimeout(() => {
        setSaveSuccess(false);
        handleClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (mappingId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('id', mappingId);
    await loadLinks();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <GranolaIcon className="w-3.5 h-3.5" />
          Granola Meeting
        </p>
        {!linksLoading && (
          <button
            onClick={handleOpen}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
          >
            <Link2 className="h-3 w-3" />
            {links.length > 0 ? 'Add another' : 'Link to meeting'}
          </button>
        )}
      </div>

      {/* Existing links */}
      {linksLoading ? (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Not linked to a Granola meeting note
        </p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => (
            <div
              key={link.externalId}
              className="flex items-center gap-2 group px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 border border-[var(--rule)]"
            >
              <GranolaIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs font-mono font-medium text-[var(--green)] dark:text-[var(--text-ter)] truncate flex-1">
                {link.externalId}
              </span>
              {link.externalUrl && (
                <a
                  href={link.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] transition-colors flex-shrink-0"
                  title="Open in Granola"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {link.mappingId && (
                <button
                  onClick={() => handleUnlink(link.mappingId!)}
                  className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Remove link"
                >
                  <Link2Off className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Inline link panel */}
      {isOpen && (
        <div className="mt-3 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50">
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-[var(--green)]" />
              Link to Granola meeting note
            </span>
            <button
              onClick={handleClose}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {/* Not connected */}
            {granolaConnected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Granola is not connected. Go to{' '}
                  <a href="/prioritization/settings/connectors" className="underline font-medium">
                    Settings → Connectors
                  </a>{' '}
                  to connect your Granola account.
                </p>
              </div>
            )}

            {granolaConnected !== false && (
              <>
                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Search meeting notes
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={itemTitle ? `e.g. "${itemTitle.slice(0, 24)}"` : 'Search by title…'}
                      className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--green)] focus:ring-2 focus:ring-green-100"
                    />
                    {notesLoading && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] animate-spin" />
                    )}
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* Empty state */}
                {!notesLoading && notes.length === 0 && !error && (
                  <p className="text-xs text-[var(--text-ter)] italic text-center py-2">
                    {searchQuery ? 'No notes match your search' : 'No meeting notes found'}
                  </p>
                )}

                {/* Notes list */}
                {notes.length > 0 && (
                  <div className="border border-[var(--rule)] overflow-hidden max-h-52 overflow-y-auto">
                    {notes.map((note) => {
                      const isSelected = selectedNote?.id === note.id;
                      return (
                        <button
                          key={note.id}
                          type="button"
                          onClick={() => setSelectedNote(isSelected ? null : note)}
                          className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs border-b border-[var(--rule)] last:border-0 transition-colors ${
                            isSelected
                              ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20'
                              : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/30'
                          }`}
                        >
                          <FileText className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate font-medium leading-tight">
                              {note.title}
                            </p>
                            <div className="flex items-center gap-1 mt-0.5 text-[var(--text-ter)]">
                              <Calendar className="h-2.5 w-2.5" />
                              <span>{formatDate(note.createdAt)}</span>
                              {note.owner?.name && (
                                <>
                                  <span>·</span>
                                  <span className="truncate max-w-[8rem]">{note.owner.name}</span>
                                </>
                              )}
                            </div>
                            {note.summary && (
                              <p className="text-[var(--text-ter)] mt-0.5 line-clamp-1 leading-tight">
                                {note.summary}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Save / Cancel */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!selectedNote || saving || saveSuccess}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--green)] hover:bg-[var(--green)] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Saving…
                      </>
                    ) : saveSuccess ? (
                      <>
                        <Check className="h-3 w-3" />
                        Linked!
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3 w-3" />
                        Link Meeting Note
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
